import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { IoCalendarClearOutline } from 'react-icons/io5';
import { TbClockPlay } from 'react-icons/tb';

import { formatTime, getChamberLabel } from '../../utils';
import { setSelectedText } from '../../features/video/videoSlice';
import { Badge } from '../ui/Badge';

const stripTags = (t) => t.replace(/<\/?strong>/gi, '');
const words = (t) => stripTags(t).trim().split(/\s+/).filter(Boolean);

// Turns an Elasticsearch highlight fragment into something the transcript can find: the highlighted
// words plus a few words of context (to pick the right occurrence), and the bare highlighted term as
// a fallback. Fragment edges can cut words in half, so a partial leading/trailing word is dropped.
const buildTranscriptQuery = (fragment) => {
  const text = String(fragment || '');
  const run = /(?:<strong>[\s\S]*?<\/strong>[\s]*)+/i.exec(text);
  if (!run) {
    const all = words(text);
    return { phrase: all.slice(1, 7).join(' ') || all.join(' '), term: '' };
  }
  const term = words(run[0]).join(' ');
  let before = words(text.slice(0, run.index));
  let after = words(text.slice(run.index + run[0].length));
  if (before.length <= 3) before = before.slice(1);
  if (after.length <= 3) after = after.slice(0, -1);
  const phrase = [...before.slice(-3), term, ...after.slice(0, 3)].join(' ');
  return { phrase, term };
};

const renderHighlight = (text) => {
  const parts = String(text).split(/(<strong>[\s\S]*?<\/strong>)/gi);
  return parts.map((part, i) => {
    const match = /^<strong>([\s\S]*?)<\/strong>$/i.exec(part);
    if (match) {
      return (
        <span key={i} className="bg-yellow-200 font-semibold text-slate-900">
          {match[1]}
        </span>
      );
    }
    return <React.Fragment key={i}>{part}</React.Fragment>;
  });
};

const SearchTable = ({ searchList }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { state } = useParams();

  const handleHighlightClick = (videoID, text) => {
    const { phrase, term } = buildTranscriptQuery(text);
    dispatch(setSelectedText(phrase));
    const params = new URLSearchParams();
    if (phrase) params.set('q', phrase);
    if (term && term !== phrase) params.set('hl', term);
    const qs = params.toString();
    navigate(`/${state}/video/${videoID}${qs ? `?${qs}` : ''}`);
  };

  return (
    <div className="overflow-hidden rounded-xl border border-slate-200">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-slate-50 text-left">
            {['Date', 'Title', 'Chamber', 'Legislature', 'Duration'].map((h) => (
              <th
                key={h}
                className="px-4 py-3 font-medium uppercase tracking-wide text-xs text-slate-500 whitespace-nowrap"
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {searchList.length > 0 &&
            searchList.map(({ video, highlight }) => (
              <React.Fragment key={video.VideoId}>
                <tr className="bg-white transition-colors duration-150 hover:bg-slate-50">
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className="inline-flex items-center gap-2 text-slate-700">
                      <IoCalendarClearOutline className="h-4 w-4 text-slate-400" />
                      {new Date(video.Date).toLocaleDateString()}
                    </span>
                  </td>
                  <td className="px-4 py-3 font-medium text-slate-900">{video.Committee}</td>
                  <td className="px-4 py-3 text-slate-700">
                    {getChamberLabel(video.Chamber)}
                  </td>
                  <td className="px-4 py-3">
                    <Badge variant="secondary">{video.Legislature || '\u2014'}</Badge>
                  </td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center gap-1 text-slate-700">
                      <TbClockPlay className="h-4 w-4 text-slate-400" />
                      {formatTime(video.Duration)}
                    </span>
                  </td>
                </tr>
                {highlight?.transcript?.length > 0 && (
                  <tr>
                    <td colSpan="5">
                      <div className="px-4 py-3 bg-slate-50 flex flex-col gap-1.5 border-t border-slate-100">
                        {highlight.transcript.map((text, i) => (
                          <button
                            key={i}
                            type="button"
                            className="text-left text-sm text-slate-600 hover:text-blue-600 hover:underline transition-colors"
                            onClick={() => handleHighlightClick(video.VideoId, text)}
                          >
                            {'\u2026'}
                            {renderHighlight(text)}
                            {'\u2026'}
                          </button>
                        ))}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
        </tbody>
      </table>
    </div>
  );
};

export default SearchTable;
